/**
 * Navigation configuration.
 * Ported from hugo.toml [[menu.main]] entries.
 *
 * This is the single source of truth for all site navigation:
 * desktop mega-menu, mobile menu, and footer columns.
 */

export interface NavItem {
  id: string;
  label: string;
  url?: string;
  icon?: string;
  description?: string;
  badge?: string;
  weight?: number;
}

export interface NavSection {
  id: string;
  label: string;
  /** Optional substring rendered in bold within the full section label. */
  labelEmphasis?: string;
  items: NavItem[];
  /** Visual column this section occupies. Sections sharing a column stack vertically. Default = sequential. */
  column?: number;
  /** "rich" shows icon + label + description; "simple" is icon + label only. Default = "rich". */
  style?: "rich" | "simple";
  /** Sub-grid columns for items within this section. Default = 1. */
  innerColumns?: number;
  gap?: "default" | "large";
}

export interface NavCategory {
  id: string;
  label: string;
  url: string;
  sections?: NavSection[];
  cta?: NavCta;
  align?: "left" | "right";
}

export interface NavCta {
  type: string;
  heading: string;
  description: string;
  image?: string;
  url: string;
  buttonText?: string;
}

// === Main navigation ===

export const MAIN_NAV: NavCategory[] = [
  {
    id: "products",
    label: "Products",
    url: "#",
    cta: {
      type: "pricing",
      heading: "See plans and pricing",
      description: "Transparent pricing designed for teams of all sizes.",
      image: "/images/nav/pricing-cta.png",
      url: "/pricing",
      buttonText: "View options",
    },
    sections: [
      {
        id: "platform-architecture",
        label: "Platform",
        labelEmphasis: "Platform",
        column: 1,
        style: "simple",
        items: [
          { id: "platform-overview", label: "C1 Identity Platform", icon: "mcp-servers", url: "/platform-overview", weight: 1 },
          { id: "c1-bridge", label: "C1 Bridge", icon: "c1-bridge", url: "/products/bridge", weight: 2 },
          { id: "integrations", label: "C1 Connectors", icon: "c1-connectors", url: "/integrations", weight: 3 },
          { id: "mcp-servers", label: "C1 MCP Servers", icon: "mcp-servers-ai", url: "/integrations/mcp-servers", weight: 4 },
        ],
      },
      {
        id: "modules",
        label: "Modules",
        column: 2,
        style: "rich",
        innerColumns: 3,
        items: [
          { id: "iga", label: "C1 Identity Governance", icon: "ai-native-identity-governance", description: "Lifecycle, access, reviews, compliance, and remediation", url: "/products/iga", weight: 1 },
          { id: "vault", label: "C1 Vault", icon: "solution-credential-security", description: "Agent ready credential vault with the unbreakable crypto", url: "/products/vault", weight: 2 },
          { id: "llm-gateway", label: "C1 LLM Gateway", icon: "ai-access-management", description: "Policy-based routing across inference providers", url: "/products/llm-gateway", weight: 3 },
          { id: "mcp-gateway", label: "C1 MCP Gateway", icon: "autonomous-worker", description: "Identity-aware policy for MCP with enforced guardrails", url: "/products/mcp-gateway", weight: 4 },
          { id: "worker", label: "C1 Agents", icon: "autonomous-worker", description: "Automate multi-step, complex enterprise work", url: "/products/agents", weight: 5 },
          { id: "apphub", label: "C1 AppHub", icon: "app-self-service", description: "Self-hosted platform for the apps your teams build", url: "/products/apphub", weight: 6 },
          { id: "egress", label: "C1 Egress", icon: "infrastructure-access-controls", description: "Default-deny outbound access with credentials injected at the wire", url: "/products/egress-proxy", weight: 7 },
        ],
      },
    ],
  },
  {
    id: "solutions",
    label: "Solutions",
    url: "#",
    sections: [
      {
        id: "identity-modernization-solutions",
        label: "Identity",
        column: 1,
        style: "rich",
        items: [
          { id: "identity-governance", label: "AI-Native Identity Governance", icon: "solution-identity-governance", description: "Get visibility, automation, and control across your environment", url: "/solutions/identity-governance", weight: 1 },
          { id: "uar-automation", label: "User Access Review Automation", icon: "solution-uar-automation", description: "Automate review scoping, decisions, remediation, and audit evidence", url: "/solutions/uar-automation", weight: 2 },
          { id: "jml-automation", label: "JML Automation", icon: "solution-jml-automation", description: "Automate onboarding, role changes, and offboarding across connected systems", url: "/solutions/jml-automation", weight: 3 },
          { id: "identity-security", label: "NHI & Identity Security", icon: "solution-identity-security", description: "Detect risk and drive response across human, NHI, and agent identities", url: "/solutions/identity-security", weight: 4 },
        ],
      },
      {
        id: "ai-enablement-solutions",
        label: "AI & Tech Enablement",
        column: 2,
        style: "rich",
        items: [
          { id: "app-ai-self-service", label: "App & MCP Self Service", icon: "solution-app-self-service", description: "Give people and agents governed access to apps, MCPs, and AI tools", url: "/solutions/self-service-app-directory", weight: 1 },
          { id: "deploy-custom-agents", label: "Deploy Custom Agents", icon: "solution-deploy-custom-agents", description: "Automate cross-system work with autonomous agents and built-in guardrails", url: "/solutions/deploy-custom-agents", weight: 2 },
          { id: "agent-sso-runtime-governance", label: "Agent SSO & Runtime Governance", icon: "solution-agent-runtime-governance", description: "Give agents delegated sign-in and policy-controlled access to tools and systems", url: "/solutions/agent-sso-runtime-governance", weight: 3 },
        ],
      },
      {
        id: "ai-security-solutions",
        label: "Security",
        column: 3,
        style: "rich",
        items: [
          { id: "shadow-ai-discovery", label: "Shadow AI Discovery", icon: "solution-shadow-ai", description: "Find and govern unsanctioned AI tools and agents", url: "/solutions/shadow-ai-discovery", weight: 1 },
          { id: "secrets-management", label: "Secrets Management", icon: "solution-credential-security", description: "Govern credentials for humans and agents", url: "/solutions/secrets-management", weight: 2 },
          { id: "jit-access", label: "JIT Access", icon: "solution-jit-access", description: "Replace standing privileges with policy-controlled, time-bound access", url: "/solutions/jit-access", weight: 3 },
        ],
      },
      {
        id: "solution-teams",
        label: "Teams",
        column: 4,
        style: "simple",
        items: [
          { id: "team-it", label: "IT", icon: "it", url: "/solutions/teams/it", weight: 1 },
          { id: "team-security", label: "Security", icon: "security-team", url: "/solutions/teams/security", weight: 2 },
          { id: "team-iam-engineer", label: "IAM Engineer", icon: "iam-engineer", url: "/solutions/teams/iam-engineer", weight: 3 },
          { id: "team-risk-compliance", label: "Risk & Compliance", icon: "risk-compliance", url: "/solutions/teams/risk-compliance", weight: 4 },
          { id: "team-ai-strategy", label: "AI Strategy & Enablement", icon: "ai-strategy", url: "/solutions/teams/ai-strategy-enablement", weight: 5 },
          { id: "team-smb", label: "Small & Mid-Size Businesses", icon: "small-mid-size-business", url: "/solutions/teams/small-mid-size-businesses", weight: 6 },
        ],
      },
    ],
  },
  {
    id: "pricing",
    label: "Pricing",
    url: "/pricing",
    // No dropdown — direct link
  },
  {
    id: "customers",
    label: "Customers",
    url: "/customers",
    // No dropdown — direct link
  },
  {
    id: "company",
    label: "Company",
    url: "#",
    align: "right",
    cta: {
      type: "workday-announcement",
      heading: "C1.ai to join Workday",
      description: "C1.ai has signed a definitive agreement to be acquired by Workday.",
      image: "/images/workday/c1-to-join-workday.webp",
      url: "/blog/c1-ai-to-join-workday",
      buttonText: "Read the announcement",
    },
    sections: [
      {
        id: "company-items",
        label: "Company",
        column: 1,
        style: "simple",
        items: [
          { id: "about-us", label: "About us", icon: "about-us", url: "/about-us", weight: 1 },
          { id: "news", label: "News", icon: "news", url: "/news", weight: 2 },
          { id: "security", label: "Security", icon: "security", url: "/security", weight: 3 },
          { id: "partners", label: "Partners", icon: "partners", url: "/partners", weight: 4 },
          { id: "careers", label: "Careers", icon: "careers", url: "/careers", weight: 5 },
        ],
      },
    ],
  },
  {
    id: "resources",
    label: "Resources",
    url: "#",
    cta: {
      type: "new-paved-road",
      heading: "The New Paved Road",
      description: "A blueprint for securing the internal apps built by everyone on your team.",
      image: "/images/new-paved-road-nav-card.webp",
      url: "/resources/new-paved-road",
      buttonText: "Get the whitepaper",
    },
    sections: [
      {
        id: "resources-resources",
        label: "Resources",
        column: 1,
        style: "simple",
        innerColumns: 2,
        items: [
          { id: "blog", label: "Blog", icon: "blog", url: "/blog", weight: 1 },
          { id: "labs", label: "C1 Labs", icon: "engineering", url: "/labs", weight: 2 },
          { id: "guides", label: "Guides", icon: "guides", url: "/guides", weight: 3 },
          { id: "glossary", label: "Glossary", icon: "glossary", url: "/glossary", weight: 5 },
          { id: "talks", label: "Talks", icon: "talks", url: "/talks", weight: 6 },
          { id: "academy", label: "C1 Academy", icon: "c1-academy", url: "/c1-academy", weight: 7 },
        ],
      },
      {
        id: "resources-docs",
        label: "Docs",
        column: 2,
        style: "simple",
        items: [
          { id: "conductorone-docs", label: "C1.ai Docs", icon: "c1-docs", url: "/docs/product/intro", weight: 1 },
          { id: "developer-docs", label: "Developer Docs", icon: "developer-docs", url: "/docs/developer/intro", weight: 2 },
          { id: "baton-docs", label: "Connector Docs", icon: "connector-docs", url: "/docs/baton/intro", weight: 3 },
        ],
      },
    ],
  },
];

// === Banner configuration ===

export const BANNER_CONFIG = {
  enabled: true,
  // Bump `id` whenever the banner content changes so prior dismissals reset.
  id: "c1-to-join-workday-2026",
  link: "/news/press-release/workday-to-acquire-c1-ai/",
  copyDesktop: "C1.ai to join Workday. Read the announcement →",
  copyMobile: "C1.ai to join Workday. Read the announcement →",
  cta: "",
  openInSameTab: true,
  // Optional partner logo shown before the copy. Decorative: the copy names
  // the partner, so it renders with empty alt text. width/height are the
  // file's intrinsic size, used to reserve space and avoid layout shift.
  logo: {
    src: "/images/workday/workday-logo-black.webp",
    width: 150,
    height: 72,
  } as { src: string; width: number; height: number } | null,
};

// === Privacy notice configuration ===
//
// Lightweight cookie *notice* shown to visitors whose country is NOT in
// PRIVACY_NOTICE_EXCLUDED_COUNTRIES (i.e. not covered by the CookieYes
// consent gate, which GTM geo-targets to the EU-27 + UK). Because consent
// is not gated for these visitors, cookies are already set; this informs
// rather than asking to opt in. Copy lives in PrivacyNotice.tsx — it embeds
// a Privacy Policy link, so it isn't a plain string here.
export const PRIVACY_NOTICE_CONFIG = {
  enabled: true,
  /** localStorage key that records the dismissal timestamp (see ACK_TTL_MS in PrivacyNotice.tsx). */
  storageKey: "c1_privacy_ack",
};

// ISO 3166-1 alpha-2 codes where CookieYes handles consent, so our notice
// stays hidden. This must mirror CookieYes's "EU Countries & UK" geo-target
// preset EXACTLY — CookieYes documents that preset as "EU member states and
// the UK", i.e. the EU-27 + GB. It does NOT cover EEA-only countries
// (Iceland, Liechtenstein, Norway) or Switzerland, so those must NOT be
// listed here — otherwise a visitor there would see neither banner.
export const PRIVACY_NOTICE_EXCLUDED_COUNTRIES = new Set<string>([
  // EU-27
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR",
  "HU", "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK",
  "SI", "ES", "SE",
  // United Kingdom
  "GB",
]);

// === Minimal-chrome pages ===
// Routes that render their own self-contained page shell (own logo bar +
// footer) and must NOT get the site-wide Banner/Header/Footer. C1 Labs owns
// its chrome at every descendant route; registration flows remain exact paths.
const MINIMAL_CHROME_PATHS = [
  "/c1-transform-2026/register",
  "/c1-transform-2026/thank-you",
  "/c1-transform-2026/vip",
] as const;

export function hasMinimalChrome(pathname: string) {
  if (pathname === "/labs" || pathname.startsWith("/labs/")) return true;
  return MINIMAL_CHROME_PATHS.includes(
    pathname as (typeof MINIMAL_CHROME_PATHS)[number],
  );
}

// === Footer columns ===
// (Already defined in Footer.tsx but could be moved here for single source of truth)

// === CTA button config ===

export const HEADER_CTAS = {
  signIn: {
    label: "Sign in",
    url: "https://accounts.conductor.one/accounts",
  },
  bookDemo: {
    label: "Book a demo",
    url: "/lp/request-demo",
  },
};
